import type { ColorThemeManifest } from "../../contracts";
import {
    THEME_COLOR_CSS_VARIABLE_TO_TOKEN,
    type ThemeColorTokenId,
} from "../../registry/color-registry";
import type { SemanticTokenId } from "../../registry/semantic-token-registry";
import {
    SYNTAX_CSS_VARIABLE_TO_TOKEN,
    type SyntaxTokenId,
} from "../../registry/syntax-token-registry";
import { BUILTIN_THEME_TOKENS } from "../../runtime/builtin-theme-tokens";

function splitCssVariables(cssVariables: Record<string, string>): {
    colors: Partial<Record<ThemeColorTokenId, string>>;
    syntaxTokens: Partial<Record<SyntaxTokenId, string>>;
} {
    const colors: Partial<Record<ThemeColorTokenId, string>> = {};
    const syntaxTokens: Partial<Record<SyntaxTokenId, string>> = {};

    for (const [cssVariable, value] of Object.entries(cssVariables)) {
        const colorToken =
            THEME_COLOR_CSS_VARIABLE_TO_TOKEN[
            cssVariable as keyof typeof THEME_COLOR_CSS_VARIABLE_TO_TOKEN
            ];
        if (colorToken) {
            colors[colorToken] = value;
            continue;
        }

        const syntaxToken =
            SYNTAX_CSS_VARIABLE_TO_TOKEN[
            cssVariable as keyof typeof SYNTAX_CSS_VARIABLE_TO_TOKEN
            ];
        if (syntaxToken) {
            syntaxTokens[syntaxToken] = value;
        }
    }

    return { colors, syntaxTokens };
}

function createTheme(
    id: keyof typeof BUILTIN_THEME_TOKENS,
    label: string,
    kind: ColorThemeManifest["kind"],
    extraColors: Partial<Record<ThemeColorTokenId, string>>,
    extraSemanticTokens: Partial<Record<SemanticTokenId, string>>,
): ColorThemeManifest<ThemeColorTokenId, SyntaxTokenId, SemanticTokenId> {
    const foundation = splitCssVariables(BUILTIN_THEME_TOKENS[id]);

    return {
        id,
        label,
        kind,
        colors: {
            ...foundation.colors,
            ...extraColors,
        },
        syntaxTokens: foundation.syntaxTokens,
        semanticTokens: extraSemanticTokens,
    };
}

export const BUILTIN_COLOR_THEMES: Array<
    ColorThemeManifest<ThemeColorTokenId, SyntaxTokenId, SemanticTokenId>
> = [
        createTheme(
            "magicmd.workbench-light",
            "Workbench Light",
            "light",
            {
                "editor.selection.background":
                    "color-mix(in srgb, var(--syntax-keyword) 30%, transparent)",
                "editor.fold.foreground":
                    "color-mix(in srgb, var(--cm-define-accent) 35%, transparent)",
                "editor.fold.hoverForeground":
                    "color-mix(in srgb, var(--cm-define-accent) 70%, transparent)",
                "editor.cursor": "#5E81AC",
                "editor.autocomplete.shadow":
                    "0 4px 16px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04)",
                "editor.hover.shadow":
                    "0 4px 16px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04)",
                "editor.completion.match": "#5E81AC",
                "content.link": "#5E81AC",
                "content.blockquote.border": "#5E81AC",
                "content.inlineRef.background": "#f1f5f9",
                "content.inlineRef.foreground": "#0f172a",
                "content.inlineRef.border": "#cbd5e1",
                "content.inlineRef.warningBackground": "#f3f4f6",
                "content.inlineRef.warningForeground": "#6b7280",
                "status.running": "var(--color-info)",
                "status.success": "var(--color-success)",
                "status.warning": "var(--color-warning)",
                "status.error": "var(--color-destructive)",
                "cm.define.accent": "#3B72B8",
                "cm.import.accent": "#7A9A6A",
                "cm.use.accent": "#6A9AAD",
                "cm.meta.accent": "#B8A260",
                "cm.content.accent": "#8B6A9E",
                "cm.render.accent": "#0E9384",
                "cm.for.accent": "#7C3AED",
                "cm.explain.accent": "#0284C7",
                "cm.ref.resolved": "#FBBF24",
                "cm.ref.definition": "#3B72B8",
                "cm.ref.scope": "#2563EB",
                "cm.ref.loop": "#7C3AED",
                "cm.ref.content": "#059669",
                "cm.ref.pending": "#B45309",
                "cm.ref.running": "#059669",
                "cm.ref.error": "#DC2626",
            },
            {
                definition: "#3B72B8",
                reference: "#2563EB",
                loopVariable: "#7C3AED",
                contentVariable: "#059669",
                warning: "#B45309",
                error: "#DC2626",
            },
        ),
        createTheme(
            "magicmd.workbench-dark",
            "Workbench Dark",
            "dark",
            {
                "editor.selection.background":
                    "color-mix(in srgb, var(--syntax-link) 32%, transparent)",
                "editor.fold.foreground":
                    "color-mix(in srgb, var(--cm-define-accent) 35%, transparent)",
                "editor.fold.hoverForeground":
                    "color-mix(in srgb, var(--cm-define-accent) 70%, transparent)",
                "editor.cursor": "#88C0D0",
                "editor.autocomplete.shadow":
                    "0 4px 24px rgba(0, 0, 0, 0.35), 0 1px 6px rgba(0, 0, 0, 0.2)",
                "editor.hover.shadow":
                    "0 4px 24px rgba(0, 0, 0, 0.35), 0 1px 6px rgba(0, 0, 0, 0.2)",
                "editor.completion.match": "#88C0D0",
                "content.link": "#88C0D0",
                "content.blockquote.border": "#88C0D0",
                "content.inlineRef.background":
                    "color-mix(in srgb, var(--color-muted) 72%, transparent)",
                "content.inlineRef.foreground": "var(--color-foreground)",
                "content.inlineRef.border":
                    "color-mix(in srgb, var(--color-border) 80%, transparent)",
                "content.inlineRef.warningBackground":
                    "color-mix(in srgb, var(--color-warning) 10%, transparent)",
                "content.inlineRef.warningForeground": "#FBBF24",
                "status.running": "var(--color-info)",
                "status.success": "var(--color-success)",
                "status.warning": "var(--color-warning)",
                "status.error": "#FCA5A5",
                "cm.define.accent": "#7EB8E2",
                "cm.import.accent": "#A3BE8C",
                "cm.use.accent": "#88C0D0",
                "cm.meta.accent": "#EBCB8B",
                "cm.content.accent": "#B48EAD",
                "cm.render.accent": "#5CC5B8",
                "cm.for.accent": "#C4B5FD",
                "cm.explain.accent": "#93C5FD",
                "cm.ref.resolved": "#FBBF24",
                "cm.ref.definition": "#7EB8E2",
                "cm.ref.scope": "#93C5FD",
                "cm.ref.loop": "#C4B5FD",
                "cm.ref.content": "#6EE7B7",
                "cm.ref.pending": "#FBBF24",
                "cm.ref.running": "#6EE7B7",
                "cm.ref.error": "#FCA5A5",
            },
            {
                definition: "#7EB8E2",
                reference: "#93C5FD",
                loopVariable: "#C4B5FD",
                contentVariable: "#6EE7B7",
                warning: "#FBBF24",
                error: "#FCA5A5",
            },
        ),
        createTheme(
            "magicmd.github-light",
            "GitHub Light",
            "light",
            {
                "editor.selection.background": "rgba(9, 105, 218, 0.18)",
                "editor.fold.foreground": "rgba(9, 105, 218, 0.34)",
                "editor.fold.hoverForeground": "rgba(9, 105, 218, 0.7)",
                "editor.cursor": "#0969DA",
                "editor.autocomplete.shadow":
                    "0 8px 24px rgba(31, 35, 40, 0.08), 0 2px 8px rgba(31, 35, 40, 0.05)",
                "editor.hover.shadow":
                    "0 8px 24px rgba(31, 35, 40, 0.08), 0 2px 8px rgba(31, 35, 40, 0.05)",
                "editor.completion.match": "#0969DA",
                "content.link": "#0969DA",
                "content.blockquote.border": "#0969DA",
                "content.inlineRef.background": "#f6f8fa",
                "content.inlineRef.foreground": "#1f2328",
                "content.inlineRef.border": "#d0d7de",
                "content.inlineRef.warningBackground": "#fff8c5",
                "content.inlineRef.warningForeground": "#9a6700",
                "status.running": "#0969DA",
                "status.success": "#1A7F37",
                "status.warning": "#9A6700",
                "status.error": "#CF222E",
                "cm.define.accent": "#0969DA",
                "cm.import.accent": "#1A7F37",
                "cm.use.accent": "#8250DF",
                "cm.meta.accent": "#BF8700",
                "cm.content.accent": "#BC4C00",
                "cm.render.accent": "#0550AE",
                "cm.for.accent": "#8250DF",
                "cm.explain.accent": "#0969DA",
                "cm.ref.resolved": "#BF8700",
                "cm.ref.definition": "#0969DA",
                "cm.ref.scope": "#0550AE",
                "cm.ref.loop": "#8250DF",
                "cm.ref.content": "#1A7F37",
                "cm.ref.pending": "#9A6700",
                "cm.ref.running": "#1A7F37",
                "cm.ref.error": "#CF222E",
            },
            {
                definition: "#0969DA",
                reference: "#0550AE",
                loopVariable: "#8250DF",
                contentVariable: "#1A7F37",
                warning: "#9A6700",
                error: "#CF222E",
            },
        ),
        createTheme(
            "magicmd.github-dark",
            "GitHub Dark",
            "dark",
            {
                "editor.selection.background": "rgba(47, 129, 247, 0.26)",
                "editor.fold.foreground": "rgba(88, 166, 255, 0.34)",
                "editor.fold.hoverForeground": "rgba(88, 166, 255, 0.7)",
                "editor.cursor": "#58A6FF",
                "editor.autocomplete.shadow":
                    "0 12px 30px rgba(1, 4, 9, 0.5), 0 3px 10px rgba(31, 111, 235, 0.2)",
                "editor.hover.shadow":
                    "0 12px 30px rgba(1, 4, 9, 0.5), 0 3px 10px rgba(31, 111, 235, 0.2)",
                "editor.completion.match": "#58A6FF",
                "content.link": "#58A6FF",
                "content.blockquote.border": "#2F81F7",
                "content.inlineRef.background": "#161b22",
                "content.inlineRef.foreground": "#e6edf3",
                "content.inlineRef.border": "#30363d",
                "content.inlineRef.warningBackground": "#2d210f",
                "content.inlineRef.warningForeground": "#d29922",
                "status.running": "#58A6FF",
                "status.success": "#3FB950",
                "status.warning": "#D29922",
                "status.error": "#F85149",
                "cm.define.accent": "#58A6FF",
                "cm.import.accent": "#3FB950",
                "cm.use.accent": "#D2A8FF",
                "cm.meta.accent": "#D29922",
                "cm.content.accent": "#FFA657",
                "cm.render.accent": "#79C0FF",
                "cm.for.accent": "#D2A8FF",
                "cm.explain.accent": "#2F81F7",
                "cm.ref.resolved": "#D29922",
                "cm.ref.definition": "#58A6FF",
                "cm.ref.scope": "#79C0FF",
                "cm.ref.loop": "#D2A8FF",
                "cm.ref.content": "#3FB950",
                "cm.ref.pending": "#D29922",
                "cm.ref.running": "#3FB950",
                "cm.ref.error": "#F85149",
            },
            {
                definition: "#58A6FF",
                reference: "#79C0FF",
                loopVariable: "#D2A8FF",
                contentVariable: "#3FB950",
                warning: "#D29922",
                error: "#F85149",
            },
        ),
        createTheme(
            "magicmd.claude-light",
            "Claude Light",
            "light",
            {
                "editor.selection.background": "rgba(201, 100, 66, 0.18)",
                "editor.fold.foreground": "rgba(201, 100, 66, 0.32)",
                "editor.fold.hoverForeground": "rgba(201, 100, 66, 0.66)",
                "editor.cursor": "#C96442",
                "editor.autocomplete.shadow":
                    "0 10px 28px rgba(65, 50, 38, 0.10), 0 2px 8px rgba(65, 50, 38, 0.06)",
                "editor.hover.shadow":
                    "0 10px 28px rgba(65, 50, 38, 0.10), 0 2px 8px rgba(65, 50, 38, 0.06)",
                "editor.completion.match": "#C96442",
                "content.link": "#9F5A3D",
                "content.blockquote.border": "#C96442",
                "content.inlineRef.background": "#f1ece3",
                "content.inlineRef.foreground": "#3f342d",
                "content.inlineRef.border": "#d8d0c3",
                "content.inlineRef.warningBackground": "#f4ead6",
                "content.inlineRef.warningForeground": "#9A5E23",
                "status.running": "#42758A",
                "status.success": "#5F7F45",
                "status.warning": "#B87333",
                "status.error": "#B43B32",
                "cm.define.accent": "#C96442",
                "cm.import.accent": "#5F7F45",
                "cm.use.accent": "#42758A",
                "cm.meta.accent": "#B87333",
                "cm.content.accent": "#9B5F86",
                "cm.render.accent": "#2F7F70",
                "cm.for.accent": "#8A6B3A",
                "cm.explain.accent": "#5F7592",
                "cm.ref.resolved": "#B87333",
                "cm.ref.definition": "#C96442",
                "cm.ref.scope": "#5F7592",
                "cm.ref.loop": "#8A6B3A",
                "cm.ref.content": "#5F7F45",
                "cm.ref.pending": "#B87333",
                "cm.ref.running": "#2F7F70",
                "cm.ref.error": "#B43B32",
            },
            {
                definition: "#C96442",
                reference: "#5F7592",
                loopVariable: "#8A6B3A",
                contentVariable: "#5F7F45",
                warning: "#B87333",
                error: "#B43B32",
            },
        ),
        createTheme(
            "magicmd.comfort-light",
            "Comfort Light",
            "light",
            {
                "editor.selection.background": "rgba(47, 111, 115, 0.18)",
                "editor.fold.foreground": "rgba(47, 111, 115, 0.32)",
                "editor.fold.hoverForeground": "rgba(47, 111, 115, 0.66)",
                "editor.cursor": "#2F6F73",
                "editor.autocomplete.shadow":
                    "0 10px 28px rgba(42, 58, 63, 0.09), 0 2px 8px rgba(42, 58, 63, 0.05)",
                "editor.hover.shadow":
                    "0 10px 28px rgba(42, 58, 63, 0.09), 0 2px 8px rgba(42, 58, 63, 0.05)",
                "editor.completion.match": "#2F6F73",
                "content.link": "#456F9C",
                "content.blockquote.border": "#2F6F73",
                "content.inlineRef.background": "#e7f0ef",
                "content.inlineRef.foreground": "#1f5156",
                "content.inlineRef.border": "#c8dad8",
                "content.inlineRef.warningBackground": "#f2e6d7",
                "content.inlineRef.warningForeground": "#92551F",
                "status.running": "#456F9C",
                "status.success": "#4C7A54",
                "status.warning": "#92551F",
                "status.error": "#B24A44",
                "cm.define.accent": "#2F6F73",
                "cm.import.accent": "#4C7A54",
                "cm.use.accent": "#456F9C",
                "cm.meta.accent": "#92551F",
                "cm.content.accent": "#7B5B86",
                "cm.render.accent": "#3A7A68",
                "cm.for.accent": "#6D6E3B",
                "cm.explain.accent": "#456F9C",
                "cm.ref.resolved": "#92551F",
                "cm.ref.definition": "#2F6F73",
                "cm.ref.scope": "#456F9C",
                "cm.ref.loop": "#6D6E3B",
                "cm.ref.content": "#4C7A54",
                "cm.ref.pending": "#92551F",
                "cm.ref.running": "#3A7A68",
                "cm.ref.error": "#B24A44",
            },
            {
                definition: "#2F6F73",
                reference: "#456F9C",
                loopVariable: "#6D6E3B",
                contentVariable: "#4C7A54",
                warning: "#92551F",
                error: "#B24A44",
            },
        ),
        createTheme(
            "magicmd.comfort-dark",
            "Comfort Dark",
            "dark",
            {
                "editor.selection.background": "rgba(134, 183, 179, 0.24)",
                "editor.fold.foreground": "rgba(134, 183, 179, 0.34)",
                "editor.fold.hoverForeground": "rgba(134, 183, 179, 0.7)",
                "editor.cursor": "#86B7B3",
                "editor.autocomplete.shadow":
                    "0 14px 34px rgba(4, 7, 10, 0.46), 0 3px 10px rgba(134, 183, 179, 0.14)",
                "editor.hover.shadow":
                    "0 14px 34px rgba(4, 7, 10, 0.46), 0 3px 10px rgba(134, 183, 179, 0.14)",
                "editor.completion.match": "#86B7B3",
                "content.link": "#8FB4D9",
                "content.blockquote.border": "#86B7B3",
                "content.inlineRef.background": "#24383b",
                "content.inlineRef.foreground": "#c9e0dc",
                "content.inlineRef.border": "#3f5659",
                "content.inlineRef.warningBackground": "#352a1b",
                "content.inlineRef.warningForeground": "#D7A75B",
                "status.running": "#8FB4D9",
                "status.success": "#93BC86",
                "status.warning": "#D7A75B",
                "status.error": "#D67C74",
                "cm.define.accent": "#86B7B3",
                "cm.import.accent": "#93BC86",
                "cm.use.accent": "#8FB4D9",
                "cm.meta.accent": "#D7A75B",
                "cm.content.accent": "#C9A4D8",
                "cm.render.accent": "#7EC7B1",
                "cm.for.accent": "#C8B17A",
                "cm.explain.accent": "#8FB4D9",
                "cm.ref.resolved": "#D7A75B",
                "cm.ref.definition": "#86B7B3",
                "cm.ref.scope": "#8FB4D9",
                "cm.ref.loop": "#C8B17A",
                "cm.ref.content": "#93BC86",
                "cm.ref.pending": "#D7A75B",
                "cm.ref.running": "#7EC7B1",
                "cm.ref.error": "#D67C74",
            },
            {
                definition: "#86B7B3",
                reference: "#8FB4D9",
                loopVariable: "#C8B17A",
                contentVariable: "#93BC86",
                warning: "#D7A75B",
                error: "#D67C74",
            },
        ),
        createTheme(
            "magicmd.solarized-light",
            "Solarized Light",
            "light",
            {
                "editor.selection.background": "rgba(38, 139, 210, 0.18)",
                "editor.fold.foreground": "rgba(108, 113, 196, 0.32)",
                "editor.fold.hoverForeground": "rgba(108, 113, 196, 0.66)",
                "editor.cursor": "#268BD2",
                "editor.autocomplete.shadow":
                    "0 8px 24px rgba(0, 43, 54, 0.10), 0 2px 8px rgba(0, 43, 54, 0.06)",
                "editor.hover.shadow":
                    "0 8px 24px rgba(0, 43, 54, 0.10), 0 2px 8px rgba(0, 43, 54, 0.06)",
                "editor.completion.match": "#268BD2",
                "content.link": "#268BD2",
                "content.blockquote.border": "#268BD2",
                "content.inlineRef.background": "#eee8d5",
                "content.inlineRef.foreground": "#657b83",
                "content.inlineRef.border": "#d3cdb9",
                "content.inlineRef.warningBackground": "#f5ecd0",
                "content.inlineRef.warningForeground": "#B58900",
                "status.running": "#268BD2",
                "status.success": "#859900",
                "status.warning": "#B58900",
                "status.error": "#DC322F",
                "cm.define.accent": "#268BD2",
                "cm.import.accent": "#859900",
                "cm.use.accent": "#2AA198",
                "cm.meta.accent": "#B58900",
                "cm.content.accent": "#D33682",
                "cm.render.accent": "#6C71C4",
                "cm.for.accent": "#6C71C4",
                "cm.explain.accent": "#268BD2",
                "cm.ref.resolved": "#B58900",
                "cm.ref.definition": "#268BD2",
                "cm.ref.scope": "#6C71C4",
                "cm.ref.loop": "#6C71C4",
                "cm.ref.content": "#859900",
                "cm.ref.pending": "#B58900",
                "cm.ref.running": "#859900",
                "cm.ref.error": "#DC322F",
            },
            {
                definition: "#268BD2",
                reference: "#6C71C4",
                loopVariable: "#6C71C4",
                contentVariable: "#859900",
                warning: "#B58900",
                error: "#DC322F",
            },
        ),
        createTheme(
            "magicmd.solarized-dark",
            "Solarized Dark",
            "dark",
            {
                "editor.selection.background": "rgba(38, 139, 210, 0.26)",
                "editor.fold.foreground": "rgba(108, 113, 196, 0.32)",
                "editor.fold.hoverForeground": "rgba(108, 113, 196, 0.66)",
                "editor.cursor": "#268BD2",
                "editor.autocomplete.shadow":
                    "0 12px 30px rgba(0, 30, 39, 0.50), 0 3px 10px rgba(38, 139, 210, 0.18)",
                "editor.hover.shadow":
                    "0 12px 30px rgba(0, 30, 39, 0.50), 0 3px 10px rgba(38, 139, 210, 0.18)",
                "editor.completion.match": "#268BD2",
                "content.link": "#268BD2",
                "content.blockquote.border": "#2AA198",
                "content.inlineRef.background": "#073642",
                "content.inlineRef.foreground": "#839496",
                "content.inlineRef.border": "#586e75",
                "content.inlineRef.warningBackground": "#2a2208",
                "content.inlineRef.warningForeground": "#B58900",
                "status.running": "#268BD2",
                "status.success": "#859900",
                "status.warning": "#B58900",
                "status.error": "#DC322F",
                "cm.define.accent": "#268BD2",
                "cm.import.accent": "#859900",
                "cm.use.accent": "#2AA198",
                "cm.meta.accent": "#B58900",
                "cm.content.accent": "#D33682",
                "cm.render.accent": "#6C71C4",
                "cm.for.accent": "#6C71C4",
                "cm.explain.accent": "#268BD2",
                "cm.ref.resolved": "#B58900",
                "cm.ref.definition": "#268BD2",
                "cm.ref.scope": "#6C71C4",
                "cm.ref.loop": "#6C71C4",
                "cm.ref.content": "#859900",
                "cm.ref.pending": "#B58900",
                "cm.ref.running": "#859900",
                "cm.ref.error": "#DC322F",
            },
            {
                definition: "#268BD2",
                reference: "#6C71C4",
                loopVariable: "#6C71C4",
                contentVariable: "#859900",
                warning: "#B58900",
                error: "#DC322F",
            },
        ),
    ];
